import {
  getLoanApplicationById,
  getLoanApplicationResponseById,
  LenderApprovalStatus,
  LenderName,
  LoanApplication,
  LoanApplicationResponse,
  LoanApplicationStatus,
} from '@nemuru-eng/js-api-client';

export class LoanApplicationEntity {
  private _value: LoanApplication;
  private _isProcessing: boolean = false;
  private _alreadyProcessed: boolean = false;

  constructor(loanApplication: LoanApplication) {
    this._value = loanApplication;
  }

  public isYes(): boolean {
    if (!this.status) return false;
    return [LenderApprovalStatus.YES].includes(this.status);
  }

  public isYesOrMaybe(): boolean {
    if (!this.status) return false;
    return [LenderApprovalStatus.YES, LenderApprovalStatus.MAYBE].includes(this.status);
  }

  public isNo(): boolean {
    if (!this.status) return false;
    return [LenderApprovalStatus.NO].includes(this.status);
  }

  public hasErrors(): boolean {
    return this.isNotProcessed() === false && this.status === undefined && this.lenderRequestId === undefined;
  }

  public isNotProcessed(): boolean {
    return this.state === undefined || this.state === LoanApplicationStatus.NOT_PROCESSED;
  }

  public isProcessed(): boolean {
    return this._value.lenderFields.loanApplicationStatus?.state === LoanApplicationStatus.PROCESSED;
  }

  public isSelected(): boolean {
    return this._value.lenderFields.loanApplicationStatus?.state === LoanApplicationStatus.SELECTED;
  }

  public isProcessedOrSelected(): boolean {
    return this.isProcessed() || this.isSelected();
  }

  public wasUpdatedRecently(): boolean {
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
    return this._value.updatedAt.getTime() > tenMinutesAgo.getTime();
  }

  public async getErrorResponse(): Promise<LoanApplicationResponse | undefined> {
    if (!this.hasErrors()) return undefined;

    return await getLoanApplicationResponseById({ loanApplicationId: this._value.id });
  }

  public async waitUntilProcessedOrSelected(): Promise<this> {
    const loanApplication = await this.getLoanApplicationIfInStates([
      LoanApplicationStatus.PROCESSED,
      LoanApplicationStatus.SELECTED,
    ]);
    this._value = loanApplication;
    return this;
  }

  protected async getLoanApplicationIfInStates(states: LoanApplicationStatus[]): Promise<LoanApplication> {
    const loanApplicationId = this.id;
    if (!loanApplicationId) {
      throw new Error('Loan application id missing to wait for processed');
    }

    let attempts = 0;
    return new Promise((resolve, reject) => {
      (async function waitForInStates() {
        attempts += 1;
        if (attempts > 30) {
          return reject();
        }

        const loanApplication = await getLoanApplicationById({ loanApplicationId });

        if (states.includes(loanApplication.lenderFields.loanApplicationStatus.state)) {
          return resolve(loanApplication);
        }

        setTimeout(waitForInStates, 2000);
      })();
    });
  }

  public set alreadyProcessed(value: boolean) {
    this._alreadyProcessed = value;
  }

  public set isProcessing(value: boolean) {
    this._isProcessing = value;
  }

  public get id(): string {
    return this._value.id;
  }

  public get lenderName(): LenderName {
    return this._value.lenderName;
  }

  public get lenderRequestId(): string | undefined {
    return this._value.lenderFields.lenderRequestId ?? undefined;
  }

  public get state(): LoanApplicationStatus | undefined {
    return this._value.lenderFields.loanApplicationStatus?.state ?? undefined;
  }

  public get status(): LenderApprovalStatus | undefined {
    return this._value.lenderFields.loanApplicationStatus?.lenderApprovalStatus ?? undefined;
  }

  public get lenderFields(): LoanApplication['lenderFields'] {
    return this._value.lenderFields;
  }

  public get isProcessing(): boolean {
    return this._isProcessing;
  }

  public get alreadyProcessed(): boolean {
    return this._alreadyProcessed;
  }
}
